import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { dataAttribute } from '../utils';

const GRID_COLUMNS = 4;

interface GridPickerProps {
  items: number[];
  selectedValue?: number;
  focusedValue?: number;
  onSelect: (value: number) => void;
  renderItem: (item: number) => React.ReactNode;
  gridColumns?: number;
  dataAttribute: dataAttribute;
  wrap?: boolean;
  ariaLabel?: string;
  isItemDisabled?: (value: number) => boolean;
}

export function GridPicker({
  items,
  selectedValue,
  focusedValue,
  onSelect,
  renderItem,
  gridColumns = GRID_COLUMNS,
  dataAttribute,
  wrap = false,
  ariaLabel,
  isItemDisabled,
}: GridPickerProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [focusedItem, setFocusedItem] = useState(focusedValue);

  const baseStyle = {
    height: `${GRID_ITEM_SIZE}px`,
    width: `${GRID_ITEM_SIZE}px`,
    boxShadow: 'none !important',
  };

  const focusedBaseStyle = {
    backgroundColor: 'rgba(var(--bs-secondary-rgb), 0.1) !important',
    color: 'var(--bs-primary) !important',
  };

  useEffect(() => {
    if (gridRef.current) {
      const selectedButton = selectedValue
        ? (gridRef.current.querySelector(
            `button[${dataAttribute}="${selectedValue}"]:not(:disabled)`,
          ) as HTMLButtonElement)
        : (gridRef.current.querySelector(
            `button[${dataAttribute}="${focusedValue}"]:not(:disabled)`,
          ) as HTMLButtonElement);

      if (selectedButton) {
        selectedButton.focus();
        setFocusedItem(selectedValue ?? focusedValue);
      }
    }
  }, [selectedValue, dataAttribute, focusedValue]);

  /**
   * Handle keyboard navigation for grid items
   */
  const handleItemKeyDown = (e: React.KeyboardEvent, item: number) => {
    // Handle selection (Enter or Space)
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item);

      return;
    }

    // Handle arrow navigation
    const currentIndex = items.indexOf(item);
    let targetIndex = -1;

    switch (e.key) {
      case 'ArrowRight':
        targetIndex = currentIndex + 1;
        break;
      case 'ArrowLeft':
        targetIndex = currentIndex - 1;
        break;
      case 'ArrowDown':
        targetIndex = currentIndex + gridColumns;
        break;
      case 'ArrowUp':
        targetIndex = currentIndex - gridColumns;
        break;
      default:
        return;
    }

    // Check bounds
    if (targetIndex < 0 || targetIndex >= items.length) {
      if (!wrap) {
        return;
      }
      // Wrap around
      if (targetIndex < 0) {
        targetIndex = items.length + targetIndex;
      } else {
        targetIndex = targetIndex % items.length;
      }
    }

    e.preventDefault();

    // Find and focus the target button
    const targetValue = items[targetIndex];

    if (!gridRef.current) {
      return;
    }

    const targetButton = gridRef.current.querySelector(
      `button[${dataAttribute}="${targetValue}"]:not(:disabled)`,
    ) as HTMLButtonElement;

    if (targetButton) {
      targetButton.focus();
    }
  };

  return (
    <div className="p-3">
      <div ref={gridRef} className="d-grid" style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)` }}>
        {items.map((item) => {
          const isSelected = item === selectedValue;
          const isDisabled = isItemDisabled?.(item) ?? false;
          const isFocused = item === focusedItem;

          return (
            <button
              key={item}
              type="button"
              aria-label={ariaLabel}
              {...{ [dataAttribute]: item }}
              onClick={() => !isDisabled && onSelect(item)}
              onKeyDown={(e) => {
                if (!isDisabled) {
                  handleItemKeyDown(e, item);
                }
              }}
              className={clsx('btn fs-4 p-0', {
                'btn-primary text-white': isSelected && !isDisabled,
                'btn-flat-primary': !isSelected || isDisabled,
              })}
              disabled={isDisabled}
              style={isFocused && !isSelected ? { ...focusedBaseStyle, ...baseStyle } : baseStyle}
              tabIndex={isFocused ? 0 : -1}
            >
              {renderItem(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const GRID_ITEM_SIZE = 70;
